import { contrastaConBlanco } from '@validate/core';
import type { Marca } from './api.js';

/**
 * La marca del cliente, aplicada a la pantalla.
 *
 * ── LA REGLA DEL CONTRASTE NO ESTÁ AQUÍ, Y ES DELIBERADO ───────────────────────
 *
 * `contrastaConBlanco` vive en `packages/core/src/verificacion/contraste.ts`. Estuvo aquí, y
 * cuando la API la necesitó para avisarle al cliente, quedó claro que no era una función de
 * pantalla: es una regla del producto que usan dos sitios.
 *
 * Dos copias de una regla es exactamente como divergen. El día que alguien ajustara el umbral
 * en uno de los dos, el panel avisaría una cosa y la pantalla haría otra — y nadie lo notaría
 * hasta que un cliente preguntara por qué su botón no se ve.
 *
 * Aquí queda solo lo que toca el DOM.
 */

// Se reexporta para que quien la busque desde el front la encuentre sin tener que saber que
// vive en el core.
export { contrastaConBlanco };

/**
 * Aplica la marca del cliente. Lo que no pase el contraste se queda con el defecto.
 *
 * El defecto NO es un fallback triste: es el diseño del Figma (#3332F2), que funciona. Que un
 * cliente vea el morado de Valídate en vez de su amarillo ilegible es infinitamente mejor que
 * un botón invisible — y él ya recibió el aviso al guardarlo, así que no es una sorpresa.
 */
export function aplicarMarca(m: Marca): void {
  const raiz = document.documentElement;

  if (m.acento !== null && contrastaConBlanco(m.acento)) {
    raiz.style.setProperty('--acento', m.acento);
    // La barra del navegador sigue al acento: es lo primero que ve la persona, antes incluso
    // de que pinte la página.
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', m.acento);
  }

  // El segundo color del degradado no lleva texto encima, así que no necesita contraste.
  if (m.acento2 !== null) raiz.style.setProperty('--acento-2', m.acento2);

  if (m.tipografia !== null) {
    raiz.style.setProperty('--tipografia', `${m.tipografia}, system-ui, sans-serif`);
  }
}
